import React, { useState, useRef,useEffect, useCallback } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Avatar,
  CircularProgress,
  Paper,
  Select,
  MenuItem
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SendIcon from '@mui/icons-material/Send';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AndroidIcon from '@mui/icons-material/Android';
import { styled } from '@mui/material/styles';
import centeredImage from '../assets/images/trailai-small-logo.png'; // Import the image
import {ThemeProvider } from '@mui/material/styles';

import customTheme from "./theme";


const StyledChatContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '80vw',
  margin: '20px auto',
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.paper, // Example: Use a light shade of your primary
}));

const StyledResponseArea = styled(Box)(({ theme }) => ({
  height: '400px',
  overflowY: 'auto',
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  backgroundColor: theme.palette.background.paper, // Example: Use a light shade of your secondary
}));

const CenteredImage = styled(Box)(({ theme }) => ({
  zIndex: 0, // Ensure the image is behind the container if needed
}));

const StyledMessageBubble = styled(Paper)(({ theme, isuser }) => ({
  padding: theme.spacing(1.5),
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(1),
  maxWidth: '80%',
  wordBreak: 'break-word',
  backgroundColor: isuser
    ? theme.palette.chatbot.user
    : theme.palette.chatbot.ai,
  color: isuser ? theme.palette.primary.contrastText : theme.palette.secondary.contrastText,
  alignSelf: isuser ? 'flex-end' : 'flex-start',
}));

const StyledInputArea = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1),
}));

function Chatbot() {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const hasWelcomeMessageHandled = useRef(false);
  const [isTyping, setIsTyping] = useState(false);
  const [chatbotArray, setChatbotArray] = useState([]);
  const [selectedChatbot, setSelectedChatbot] = useState(''); // State to hold the selected value
  const chatAreaRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const handleSendWelcomeMessage  = useCallback(async (chatbot_id) => {
    setIsTyping(true);
    try {
      const authToken = `Bearer ${localStorage.getItem('authToken')}`;
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL; 
      const url = new URL(`${API_BASE_URL}/hrbot/chatbot/create_welcome_message_using_token/${chatbot_id}`)
      const response = await fetch(url.toString(), { 
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          "ngrok-skip-browser-warning": "69420",
          "Authorization": authToken,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Error fetching AI response:', errorData);
        const errorMessage = errorData?.message || `Failed to get response from AI (status: ${response.status})`;
        const errorResponse = {
          text: errorMessage,
          sender: 'ai',
          error: true, 
        };
        setMessages((prevMessages) => [...prevMessages, errorResponse]);
      } else {
        const aiResponseData = await response.json();
        const aiResponse = {
          text: aiResponseData.message, // Adjust 'response' to the actual key in your AI's JSON response
          sender: 'ai',
        };
        setMessages((prevMessages) => [...prevMessages, aiResponse]);
      }
    } catch (error) {
      console.error('Error sending message and fetching response:', error);
      const errorResponse = {
        text: 'Failed to communicate with the AI.',
        sender: 'ai',
        error: true,
      };
      setMessages((prevMessages) => [...prevMessages, errorResponse]);
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } finally {
      setIsTyping(false);
      if (chatAreaRef.current) {
        chatAreaRef.current.scrollTop = chatAreaRef.current.scrollHeight;
      }
    }
  }, [navigate]);

  // Scroll to bottom whenever messages update
  useEffect(() => {
    if (chatAreaRef.current) {
      chatAreaRef.current.scrollTop = chatAreaRef.current.scrollHeight;
    }
    inputRef.current.focus();
  }, [messages]); // Run this effect whenever the 'messages' array changes
  
  useEffect(() => {

    // Retrieve data from localStorage when the component mounts
    const storedChatbots = localStorage.getItem('chatbots');

    if (storedChatbots) {
      try {
        const parsedChatbots = JSON.parse(storedChatbots);
        setChatbotArray(parsedChatbots);

        if (Array.isArray(parsedChatbots) && parsedChatbots.length > 0) {
          const firstChatbot = parsedChatbots[0];
          setSelectedChatbot(firstChatbot.chatbot_id);

          if (!hasWelcomeMessageHandled.current) {
            console.log('First render: Calling handleSendWelcomeMessage');
            handleSendWelcomeMessage(firstChatbot.chatbot_id);
            hasWelcomeMessageHandled.current = true;
          } else {
            console.log('Subsequent render (or Strict Mode second run): Skipping handleSendWelcomeMessage');
          }
        }
      } catch (error) {
        console.error('Error parsing chatbots from localStorage:', error);
        // Optionally set a default or empty array if parsing fails
        setSelectedChatbot([]);
      }
    } else {
      // If 'chatbots' doesn't exist in localStorage, set a default or empty array
      setSelectedChatbot([]);
    }
  }, [handleSendWelcomeMessage, selectedChatbot]);

  const handleSendMessage = async () => {
    if (inputText.trim()) {
      const newUserMessage = { text: inputText, sender: 'user' };
      setMessages((prevMessages) => [...prevMessages, newUserMessage]);
      if (chatAreaRef.current) {
        chatAreaRef.current.scrollTop = chatAreaRef.current.scrollHeight;
      }
      setInputText('');
      setIsTyping(true);

      try {
        const authToken = `Bearer ${localStorage.getItem('authToken')}`;
        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL; 
        const url = new URL(`${API_BASE_URL}/hrbot/chatbot/send_message_to_chatbot_using_token/${selectedChatbot}`)
        url.searchParams.append('query_text', inputText);
        const response = await fetch(url.toString(), { 
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            "ngrok-skip-browser-warning": "69420",
            "Authorization": authToken,
          },
        });
  
        if (!response.ok) {
          const errorData = await response.json();
          console.error('Error fetching AI response:', errorData);
          const errorMessage = errorData?.message || `Failed to get response from AI (status: ${response.status})`;
          const errorResponse = {
            text: errorMessage,
            sender: 'ai',
            error: true, 
          };
          setMessages((prevMessages) => [...prevMessages, errorResponse]);
        } else {
          const aiResponseData = await response.json();
          const aiResponse = {
            text: aiResponseData.message, // Adjust 'response' to the actual key in your AI's JSON response
            sender: 'ai',
          };
          setMessages((prevMessages) => [...prevMessages, aiResponse]);
        }
      } catch (error) {
        console.error('Error sending message and fetching response:', error);
        const errorResponse = {
          text: 'Failed to communicate with the AI.',
          sender: 'ai',
          error: true,
        };
        setMessages((prevMessages) => [...prevMessages, errorResponse]);
      } finally {
        setIsTyping(false);
        if (chatAreaRef.current) {
          chatAreaRef.current.scrollTop = chatAreaRef.current.scrollHeight;
        }
      }
    }
  };

  const handleInputChange = (event) => {
    setInputText(event.target.value);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleSendMessage();
    }
  };

  const handleChatbotChange = (event) => {
    setMessages([]);
    setSelectedChatbot(event.target.value);
    console.log('Selected chatbot ID:', event.target.value);
    handleSendWelcomeMessage(event.target.value)
  };

  return (
    <ThemeProvider theme={customTheme}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center', // Vertically centers children
          gap: 2, // Optional: Adds spacing between the boxes (using theme spacing)
        }}
      >
        <CenteredImage>
          <img src={centeredImage} style={{ maxWidth: '100%', maxHeight: '80vh' }} alt="" />
        </CenteredImage>
        <StyledChatContainer>
          <Select
            labelId="demo-simple-select-label"
            id="demo-simple-select"
            label="Age"
            value={selectedChatbot}
            onChange={handleChatbotChange} 
            sx={{ padding: 1, marginBottom: 2 }}
          >
            {chatbotArray.map((option) => (
              <MenuItem key={option.chatbot_name} value={option.chatbot_id}>
                {option.chatbot_label}
              </MenuItem>
            ))}
          </Select>
          <StyledResponseArea ref={chatAreaRef}>
            {messages.map((message, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                <Avatar sx={{ mr: 1 }}>
                  {message.sender === 'user' ? <AccountCircleIcon /> : <SmartToyIcon />}
                </Avatar>
                <StyledMessageBubble isuser={message.sender === 'user'}>
                  <Typography variant="body1">{message.text}</Typography>
                </StyledMessageBubble>
              </Box>
            ))}
            {isTyping && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Avatar sx={{ mr: 1 }}>
                  <AndroidIcon />
                </Avatar>
                <CircularProgress size={24} />
                <Typography variant="body2" sx={{ ml: 1, fontStyle: 'italic', color: 'text.secondary' }}>
                  Thinking...
                </Typography>
              </Box>
            )}
            {!messages.length && (
              <Typography variant="subtitle1" color="text.secondary" align="center">
                Start a conversation!
              </Typography>
            )}
          </StyledResponseArea>

          <StyledInputArea>
            <TextField
              fullWidth
              inputRef={inputRef}
              variant="outlined"
              label="Ask me anything for HR"
              value={inputText}
              onChange={handleInputChange}
              disabled={isTyping}
              onKeyDown={handleKeyDown}
            />
            <Button
              variant="contained"
              color="primary"
              endIcon={<SendIcon />}
              onClick={handleSendMessage}
              disabled={isTyping}
            >
              Send
            </Button>
          </StyledInputArea>
        </StyledChatContainer>
      </Box>
    </ThemeProvider>
  );
}

export default Chatbot;